import React, {
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';

import './OpportunityEditorPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const REVIEW_URL =
  `${API_BASE_URL}/api/v1/opportunity_reviews`;

const EMPTY_FORM = {
  title_en: '',
  title_fr: '',
  summary_en: '',
  summary_fr: '',
  organization: '',
  opportunity_type: 'other',
  target_audience: '',
  geography: '',
  deadline: '',
  application_url: '',
  source_name: 'ACE Manual Entry',
  source_url: '',
  featured: false,
};

const TYPE_OPTIONS = [
  ['grant', 'Grant'],
  ['fellowship', 'Fellowship'],
  ['accelerator', 'Accelerator'],
  ['investment', 'Investment'],
  ['training', 'Training'],
  ['market', 'Market opportunity'],
  ['procurement', 'Procurement'],
  ['job', 'Job'],
  ['competition', 'Competition'],
  ['other', 'Other'],
];

const toDateTimeLocal = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (number) =>
    String(number).padStart(2, '0');

  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
};

const OpportunityEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isNew = !id;

  const [form, setForm] =
    useState(EMPTY_FORM);

  const [status, setStatus] =
    useState(isNew ? 'ready' : 'loading');

  const [message, setMessage] =
    useState('');

  const [error, setError] =
    useState('');

  useEffect(() => {
    if (isNew) return;

    const loadOpportunity = async () => {
      try {
        const response = await axios.get(
          `${REVIEW_URL}/${id}`
        );

        const opportunity = response.data;

        setForm({
          title_en:
            opportunity.title_en || '',
          title_fr:
            opportunity.title_fr || '',
          summary_en:
            opportunity.summary_en || '',
          summary_fr:
            opportunity.summary_fr || '',
          organization:
            opportunity.organization || '',
          opportunity_type:
            opportunity.opportunity_type ||
            'other',
          target_audience:
            opportunity.target_audience || '',
          geography:
            opportunity.geography || '',
          deadline:
            toDateTimeLocal(
              opportunity.deadline
            ),
          application_url:
            opportunity.application_url ||
            '',
          source_name:
            opportunity.source_name || '',
          source_url:
            opportunity.source_url || '',
          featured:
            Boolean(opportunity.featured),
        });

        setStatus('ready');
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
          requestError.message ||
          'Unable to load opportunity.'
        );

        setStatus('failed');
      }
    };

    loadOpportunity();
  }, [id, isNew]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((current) => ({
      ...current,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));
  };

  const payload = () => ({
    opportunity: {
      ...form,
      deadline:
        form.deadline || null,
      source_url:
        form.source_url ||
        form.application_url,
    },
  });

  const saveDraft = async () => {
    try {
      setStatus('saving');
      setError('');
      setMessage('');

      if (isNew) {
        const response =
          await axios.post(
            REVIEW_URL,
            payload()
          );

        setMessage(
          'Opportunity created as draft.'
        );

        navigate(
          `/admin/opportunities/${response.data.id}/edit`,
          { replace: true }
        );

        return;
      }

      await axios.patch(
        `${REVIEW_URL}/${id}`,
        payload()
      );

      setMessage('Draft saved.');
      setStatus('ready');
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        requestError.response?.data?.errors
          ?.join(', ') ||
        requestError.message ||
        'Unable to save opportunity.'
      );

      setStatus('ready');
    }
  };

  const publishOpportunity =
    async () => {
      if (isNew) {
        setError(
          'Save the opportunity as a draft before publishing.'
        );
        return;
      }

      try {
        setStatus('saving');
        setError('');
        setMessage('');

        await axios.patch(
          `${REVIEW_URL}/${id}/publish`,
          payload()
        );

        navigate('/admin/opportunities');
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
          requestError.message ||
          'Unable to publish opportunity.'
        );

        setStatus('ready');
      }
    };

  const rejectOpportunity = async () => {
    if (isNew) {
      navigate('/admin/opportunities');
      return;
    }

    try {
      setStatus('saving');

      await axios.patch(
        `${REVIEW_URL}/${id}/reject`
      );

      navigate('/admin/opportunities');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to reject opportunity.'
      );

      setStatus('ready');
    }
  };

  if (status === 'loading') {
    return (
      <main className="admin-page">
        <p>Loading opportunity...</p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Opportunities
          </p>

          <h1>
            {isNew
              ? 'Add opportunity'
              : 'Edit opportunity'}
          </h1>

          <p>
            Review, enrich and prepare the
            opportunity before publication.
          </p>
        </div>

        <Link
          to="/admin/opportunities"
          className="admin-secondary-link"
        >
          ← Opportunities
        </Link>
      </header>

      {error && (
        <div className="editor-message error">
          {error}
        </div>
      )}

      {message && (
        <div className="editor-message success">
          {message}
        </div>
      )}

      <section className="opportunity-editor">
        <div className="editor-section-heading">
          <span>01</span>

          <div>
            <h2>Bilingual presentation</h2>

            <p>
              Edit the English and French
              content presented by ACE.
            </p>
          </div>
        </div>

        <div className="editor-grid">
          <label className="editor-field">
            <span>English title</span>

            <textarea
              name="title_en"
              value={form.title_en}
              onChange={handleChange}
              rows="3"
            />
          </label>

          <label className="editor-field">
            <span>French title</span>

            <textarea
              name="title_fr"
              value={form.title_fr}
              onChange={handleChange}
              rows="3"
            />
          </label>

          <label className="editor-field">
            <span>English summary</span>

            <textarea
              name="summary_en"
              value={form.summary_en}
              onChange={handleChange}
              rows="8"
            />
          </label>

          <label className="editor-field">
            <span>French summary</span>

            <textarea
              name="summary_fr"
              value={form.summary_fr}
              onChange={handleChange}
              rows="8"
            />
          </label>
        </div>

        <div className="editor-divider" />

        <div className="editor-section-heading">
          <span>02</span>

          <div>
            <h2>
              Classification and eligibility
            </h2>

            <p>
              Add the information users need
              to evaluate the opportunity.
            </p>
          </div>
        </div>

        <div className="editor-grid">
          <label className="editor-field">
            <span>Organization</span>

            <input
              type="text"
              name="organization"
              value={form.organization}
              onChange={handleChange}
            />
          </label>

          <label className="editor-field">
            <span>Opportunity type</span>

            <select
              name="opportunity_type"
              value={form.opportunity_type}
              onChange={handleChange}
            >
              {TYPE_OPTIONS.map(
                ([value, label]) => (
                  <option
                    value={value}
                    key={value}
                  >
                    {label}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="editor-field">
            <span>Target audience</span>

            <input
              type="text"
              name="target_audience"
              value={form.target_audience}
              onChange={handleChange}
              placeholder="MSMEs, researchers, youth..."
            />
          </label>

          <label className="editor-field">
            <span>Geography</span>

            <input
              type="text"
              name="geography"
              value={form.geography}
              onChange={handleChange}
              placeholder="Mali, Africa, Global..."
            />
          </label>

          <label className="editor-field">
            <span>Deadline</span>

            <input
              type="datetime-local"
              name="deadline"
              value={form.deadline}
              onChange={handleChange}
            />
          </label>

          <label className="editor-checkbox">
            <input
              type="checkbox"
              name="featured"
              checked={form.featured}
              onChange={handleChange}
            />

            <span>
              Feature this opportunity
            </span>
          </label>
        </div>

        <div className="editor-divider" />

        <div className="editor-section-heading">
          <span>03</span>

          <div>
            <h2>Source and application</h2>

            <p>
              Preserve provenance while
              directing users to the original
              opportunity.
            </p>
          </div>
        </div>

        <div className="editor-grid">
          <label className="editor-field">
            <span>Application URL</span>

            <input
              type="url"
              name="application_url"
              value={form.application_url}
              onChange={handleChange}
            />
          </label>

          <label className="editor-field">
            <span>Source name</span>

            <input
              type="text"
              name="source_name"
              value={form.source_name}
              onChange={handleChange}
            />
          </label>

          <label className="editor-field editor-full">
            <span>Original source URL</span>

            <input
              type="url"
              name="source_url"
              value={form.source_url}
              onChange={handleChange}
            />

            {form.source_url && (
              <a
                href={form.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="editor-source-link"
              >
                Open original source ↗
              </a>
            )}
          </label>
        </div>

        <footer className="editor-actions">
          <button
            type="button"
            className="editor-button draft"
            onClick={saveDraft}
            disabled={status === 'saving'}
          >
            Save draft
          </button>

          {!isNew && (
            <button
              type="button"
              className="editor-button publish"
              onClick={
                publishOpportunity
              }
              disabled={
                status === 'saving'
              }
            >
              Publish
            </button>
          )}

          <button
            type="button"
            className="editor-button reject"
            onClick={rejectOpportunity}
            disabled={status === 'saving'}
          >
            {isNew ? 'Cancel' : 'Reject'}
          </button>
        </footer>
      </section>
    </main>
  );
};

export default OpportunityEditorPage;
